import { useCallback, useMemo } from 'react';
import { useQueryState, parseAsString } from 'nuqs';
import { getLocalTimeZone, today, parseDate } from '@internationalized/date';
import { useServicesQuery } from '@/services/api';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { isEmptyString } from '@/utils/string';
import type { DeliverabilityDateRange, DeliverabilityMetricRow } from './types';
import { transformMetricsToRows, hasNoData } from './utils/metrics';
import { FIRST_POSSIBLE_DATE } from './utils/constants';

const thirtyDaysAgo = today(getLocalTimeZone()).subtract({ days: 30 });

export function useDeliverabilityData() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const [startTime, setStartTime] = useQueryState('start_time', parseAsString);
  const [endTime, setEndTime] = useQueryState('end_time', parseAsString);
  const [search, setSearch] = useQueryState('search', parseAsString);
  const [sortBy, setSortBy] = useQueryState('sort_by', parseAsString);
  const [sortDirection, setSortDirection] = useQueryState('sort_dir', parseAsString);

  const dateRange = useMemo<DeliverabilityDateRange>(() => {
    const todayDate = today(getLocalTimeZone());
    let startDate = FIRST_POSSIBLE_DATE.compare(thirtyDaysAgo) > 0 ? FIRST_POSSIBLE_DATE : thirtyDaysAgo;
    let endDate = todayDate;

    if (!isEmptyString(startTime)) {
      try {
        const parsedDate = parseDate(startTime);
        startDate = FIRST_POSSIBLE_DATE.compare(parsedDate) > 0 ? FIRST_POSSIBLE_DATE : parsedDate;
      } catch {
        // Invalid date, use default
      }
    }

    if (!isEmptyString(endTime)) {
      try {
        const parsedDate = parseDate(endTime);
        endDate = todayDate.compare(parsedDate) < 0 ? todayDate : parsedDate;
      } catch {
        // Invalid date, use default
      }
    }

    return { start: startDate, end: endDate };
  }, [startTime, endTime]);

  const metricsQuery = useServicesQuery(
    'getDeliverabilityMetrics',
    {
      params: { environment_id: workspaceId ?? 0 },
      query: {
        version: '2',
        start: Math.floor(dateRange.start.toDate(getLocalTimeZone()).getTime() / 1000),
        end: Math.floor(dateRange.end.toDate(getLocalTimeZone()).getTime() / 1000),
        res: 'days',
      },
    },
    { staleTime: 1000 * 60 * 5, enabled: workspaceId != null },
  );

  const rows = useMemo<DeliverabilityMetricRow[]>(() => {
    if (metricsQuery.data?.inbox_provider_metrics == null) {
      return [];
    }
    return transformMetricsToRows(metricsQuery.data.inbox_provider_metrics);
  }, [metricsQuery.data]);

  const handleDateRangeChange = useCallback(
    (newRange: DeliverabilityDateRange) => {
      void setStartTime(newRange.start.toString());
      void setEndTime(newRange.end.toString());
    },
    [setStartTime, setEndTime],
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      void setSearch(isEmptyString(searchTerm) ? null : searchTerm);
    },
    [setSearch],
  );

  const sortingValue = useMemo(() => {
    if (isEmptyString(sortBy)) {
      // Default sort by outbound amount (sentPercentage) in descending order
      return { id: 'sentPercentage', desc: true };
    }
    return { id: sortBy, desc: sortDirection === 'desc' };
  }, [sortBy, sortDirection]);

  const handleSortingChange = useCallback(
    (newSorting: { id: string; desc: boolean }) => {
      void setSortBy(newSorting.id);
      void setSortDirection(newSorting.desc ? 'desc' : 'asc');
    },
    [setSortBy, setSortDirection],
  );

  return {
    rows,
    dateRange,
    searchValue: search ?? '',
    sortingValue,
    isLoading: metricsQuery.isLoading,
    isError: metricsQuery.isError,
    error: metricsQuery.error,
    hasNoData: hasNoData(rows),
    handleDateRangeChange,
    handleSearch,
    handleSortingChange,
  };
}

export function useHasMailServers() {
  const workspaceId = getWorkspaceIdFromCurrentUrl();

  const mailServersQuery = useServicesQuery(
    'getMailServers',
    { params: { environment_id: workspaceId ?? 0 } },
    { enabled: workspaceId != null },
  );

  return {
    hasMailServers: (mailServersQuery.data?.mail_servers.length ?? 0) > 0,
    isLoading: mailServersQuery.isLoading,
  };
}
